import React, { useState, useEffect } from "react";
import {
    Box,
    Text,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Badge,
    useColorModeValue,
    Container,
    Heading,
    HStack,
    Icon,
    Skeleton,
    SkeletonText
} from "@chakra-ui/react";
import { FaTrophy, FaMedal, FaAward } from "react-icons/fa";
import { Helmet } from "react-helmet";

export default function Leaderboard() {
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [animatedAmounts, setAnimatedAmounts] = useState({});
    const [updateCount, setUpdateCount] = useState(0);
    const [nextUpdateIn, setNextUpdateIn] = useState(10);

    const bg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.700", "white");
    const headerBg = useColorModeValue("gray.100", "gray.700");
    const borderColor = useColorModeValue("gray.200", "gray.600");

    // Expanded player pool for live shuffling
    const playerPool = {
        firstNames: [
            "Alex", "Sarah", "Mike", "Emma", "David", "Lisa", "John", "Maria", "Chris", "Ana",
            "James", "Sophie", "Ryan", "Jessica", "Kevin", "Amanda", "Brian", "Nicole", "Tyler", "Ashley",
            "Daniel", "Rachel", "Matthew", "Lauren", "Andrew", "Stephanie", "Mark", "Rebecca", "Steven", "Michelle",
            "Joshua", "Jennifer", "Jason", "Samantha", "Nathan", "Elizabeth", "Jacob", "Melissa", "Ryan", "Amy",
            "Anthony", "Brittany", "William", "Danielle", "Christopher", "Heather", "Jonathan", "Megan", "Nicholas", "Kimberly"
        ],
        lastNames: [
            "Johnson", "Smith", "Williams", "Brown", "Davis", "Miller", "Wilson", "Moore", "Taylor", "Anderson",
            "Thomas", "Jackson", "White", "Harris", "Martin", "Thompson", "Garcia", "Martinez", "Robinson", "Clark",
            "Rodriguez", "Lewis", "Lee", "Walker", "Hall", "Allen", "Young", "Hernandez", "King", "Wright",
            "Lopez", "Hill", "Scott", "Green", "Adams", "Baker", "Gonzalez", "Nelson", "Carter", "Mitchell",
            "Perez", "Roberts", "Turner", "Phillips", "Campbell", "Parker", "Evans", "Edwards", "Collins", "Stewart"
        ],
        usernamePrefixes: [
            "Pro", "King", "Legend", "Master", "Elite", "Star", "Champion", "Winner", "Lucky", "Diamond",
            "Royal", "Supreme", "Ultra", "Mega", "Super", "Prime", "Alpha", "Beta", "Omega", "Phoenix",
            "Dragon", "Tiger", "Eagle", "Wolf", "Lion", "Shark", "Viper", "Falcon", "Hawk", "Bear",
            "Shadow", "Ghost", "Storm", "Blaze", "Thunder", "Lightning", "Fire", "Ice", "Steel", "Gold"
        ],
        usernameSuffixes: [
            "Gamer", "Player", "Bet", "Win", "777", "Ace", "Boss", "Pro", "Max", "VIP",
            "King", "Lord", "God", "Master", "Ninja", "Warrior", "Hunter", "Knight", "Wizard", "Mage",
            "Hero", "Legend", "Champion", "Winner", "Victor", "Conqueror", "Destroyer", "Slayer", "Beast", "Monster",
            "X", "88", "99", "123", "007", "666", "911", "420", "247", "365"
        ],
        games: [
            "Blackjack Premium", "Roulette Royale", "Poker Championship", "Baccarat Elite", "Slots Mega Win",
            "Live Casino Hold'em", "Dragon Tiger", "Lightning Roulette", "Dream Catcher", "Crazy Time",
            "Monopoly Live", "Deal or No Deal", "Gonzo's Quest", "Starburst", "Book of Dead",
            "Sweet Bonanza", "Gates of Olympus", "Pragmatic Slots", "Evolution Gaming", "NetEnt Live",
            "Mega Ball", "Cash or Crash", "Adventures Beyond Wonderland", "Fan Tan", "Sic Bo",
            "Andar Bahar", "Teen Patti", "Caribbean Stud", "Three Card Poker", "Ultimate Texas Hold'em"
        ]
    };

    // Generate random leaderboard data
    const generateLeaderboard = () => {
        const data = [];
        const usedUsernames = new Set();

        for (let i = 0; i < 10; i++) {
            let username;
            do {
                const prefix = playerPool.usernamePrefixes[Math.floor(Math.random() * playerPool.usernamePrefixes.length)];
                const suffix = playerPool.usernameSuffixes[Math.floor(Math.random() * playerPool.usernameSuffixes.length)];
                const number = Math.floor(Math.random() * 999) + 1;
                username = `${prefix}${suffix}${number}`;
            } while (usedUsernames.has(username));

            usedUsernames.add(username);

            const firstName = playerPool.firstNames[Math.floor(Math.random() * playerPool.firstNames.length)];
            const lastName = playerPool.lastNames[Math.floor(Math.random() * playerPool.lastNames.length)];

            // Create more dramatic win amounts with occasional jackpots
            let winAmount;
            const jackpotChance = Math.random();
            if (jackpotChance < 0.1) { // 10% chance for mega jackpot
                winAmount = Math.floor(Math.random() * (50000 - 25000 + 1)) + 25000;
            } else if (jackpotChance < 0.25) { // 15% chance for big win
                winAmount = Math.floor(Math.random() * (25000 - 10000 + 1)) + 10000;
            } else { // Regular wins
                winAmount = Math.floor(Math.random() * (9999 - 500 + 1)) + 500;
            }

            const game = playerPool.games[Math.floor(Math.random() * playerPool.games.length)];

            data.push({
                rank: i + 1,
                username: username,
                fullName: `${firstName} ${lastName}`,
                winAmount: winAmount,
                game: game
            });
        }

        // Sort by win amount in descending order
        return data.sort((a, b) => b.winAmount - a.winAmount).map((item, index) => ({
            ...item,
            rank: index + 1
        }));
    };

    // Initial load effect
    useEffect(() => {
        setIsLoading(true);

        // Initial load delay
        setTimeout(() => {
            const data = generateLeaderboard();
            setLeaderboardData(data);
            setIsLoading(false);

            // Animate number counting for each player
            data.forEach((player, index) => {
                setTimeout(() => {
                    animateNumberCountUp(player.username, player.winAmount);
                }, index * 200);
            });
        }, 1000);
    }, []);

    // Live update effect - updates every 10 seconds
    useEffect(() => {
        if (isLoading) return;

        const updateData = () => {
            const newData = generateLeaderboard();
            setLeaderboardData(newData);
            setUpdateCount(prev => prev + 1);

            const tableElement = document.querySelector('[data-leaderboard-table]');
            if (tableElement) {
                tableElement.style.animation = 'flashUpdate 0.1s ease-out';
                setTimeout(() => {
                    tableElement.style.animation = '';
                });
            }

            newData.forEach((player, index) => {
                setTimeout(() => {
                    animateNumberCountUp(player.username, player.winAmount);
                }, index * 100);
            });
        };

        let counter = 10;
        setNextUpdateIn(counter);

        const countdownInterval = setInterval(() => {
            if (counter > 1) {
                counter -= 1;
            } else {
                updateData();
                counter = 10; // reset 10 seconds
            }
            setNextUpdateIn(counter);
        }, 1000);

        return () => clearInterval(countdownInterval);
    }, [isLoading]);


    // Animate number counting up
    const animateNumberCountUp = (username, finalAmount) => {
        const duration = 1000; // 2 seconds
        const steps = 50;
        const increment = finalAmount / steps;
        let currentAmount = 0;

        const timer = setInterval(() => {
            currentAmount += increment;
            if (currentAmount >= finalAmount) {
                currentAmount = finalAmount;
                clearInterval(timer);
            }

            setAnimatedAmounts(prev => ({
                ...prev,
                [username]: Math.floor(currentAmount)
            }));
        }, duration / steps);
    };

    const getRankIcon = (rank) => {
        const iconProps = {
            boxSize: 6,
        };

        if (rank === 1) return (
            <Icon
                as={FaTrophy}
                color="gold"
                {...iconProps}
                filter="drop-shadow(0 0 8px gold)"
                animation="pulse 2s infinite"
                sx={{
                    '@keyframes pulse': {
                        '0%, 100%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' }
                    }
                }}
            />
        );
        if (rank === 2) return (
            <Icon
                as={FaMedal}
                color="silver"
                {...iconProps}
                filter="drop-shadow(0 0 6px silver)"
                animation="pulse 2s infinite"
                sx={{
                    '@keyframes pulse': {
                        '0%, 100%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' }
                    }
                }}
            />
        );
        if (rank === 3) return (
            <Icon
                as={FaAward}
                color="#CD7F32"
                {...iconProps}
                filter="drop-shadow(0 0 4px #CD7F32)"
                animation="pulse 2s infinite"
                sx={{
                    '@keyframes pulse': {
                        '0%, 100%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.1)' }
                    }
                }}
            />
        );
        return (
            <Text
                fontSize="lg"
                fontWeight="bold"
            >
                {rank}
            </Text>
        );
    };

    const getRankLevel = (rank) => {
        if (rank === 1)
            return <Badge bg="#000000" color="white" fontSize="sm">Elite Black</Badge>;
        if (rank === 2)
            return <Badge bg="cyan.400" color="white" fontSize="sm">Diamond</Badge>;
        if (rank === 3)
            return <Badge bg="cyan.400" color="white" fontSize="sm">Platinum</Badge>;
        if (rank === 4 || rank === 5)
            return <Badge bg="yellow.400" color="white" fontSize="sm">Gold</Badge>;
        if (rank === 6 || rank === 7)
            return <Badge bg="gray.400" color="white" fontSize="sm">Silver</Badge>;
        if (rank === 8 || rank === 9 || rank === 10)
            return <Badge bg="orange.400" color="white" fontSize="sm">Bronze</Badge>;

        return <Badge bg="orange.400" color="white" fontSize="sm">Bronze</Badge>;
    };

    // Define hover styles
    const hoverBg = useColorModeValue("gray.50", "gray.700");

    return (
        <>
            <Helmet>
                {/* GTM en <head> */}
                <script>
                    {`
            (function(w,d,s,l,i){w[l]=w[l]||[];
              w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-K32LWXGQ');
          `}
                </script>
            </Helmet>

            {/* GTM después del <body> (noscript) */}
            <noscript>
                <iframe
                    src="https://www.googletagmanager.com/ns.html?id=GTM-K32LWXGQ"
                    height="0"
                    width="0"
                    style={{ display: "none", visibility: "hidden" }}
                />
            </noscript>
            <Box bg={bg} py={8}>
                <Container maxW="6xl">
                    <VStack spacing={6} align="stretch">
                        <Box textAlign="center">
                            <Heading
                                size="2xl"
                                color={textColor}
                                mb={2}
                                bgGradient="linear(to-r, gold, yellow.400)"
                                bgClip="text"
                                animation="slideInUp 1s ease-out"
                                sx={{
                                    '@keyframes slideInUp': {
                                        from: { transform: 'translateY(50px)', opacity: 0 },
                                        to: { transform: 'translateY(0)', opacity: 1 }
                                    }
                                }}
                            >
                                🏆 Top Winners Leaderboard
                            </Heading>
                            <Text
                                fontSize="lg"
                                color="gray.500"
                                animation="slideInUp 1s ease-out 0.2s both"
                                sx={{
                                    '@keyframes slideInUp': {
                                        from: { transform: 'translateY(50px)', opacity: 0 },
                                        to: { transform: 'translateY(0)', opacity: 1 }
                                    }
                                }}
                            >
                                The biggest winners at WagerStreet Casino
                            </Text>
                            <HStack
                                justify="center"
                                mt={4}
                                animation="slideInUp 1s ease-out 0.3s both"
                                sx={{
                                    '@keyframes slideInUp': {
                                        from: { transform: 'translateY(50px)', opacity: 0 },
                                        to: { transform: 'translateY(0)', opacity: 1 }
                                    }
                                }}
                            >
                                <Badge
                                    colorScheme="green"
                                    variant="solid"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    animation="pulse 2s infinite"
                                    sx={{
                                        '@keyframes pulse': {
                                            '0%, 100%': { opacity: 1 },
                                            '50%': { opacity: 0.6 }
                                        }
                                    }}
                                >
                                    🔴 LIVE
                                </Badge>
                                <Text fontSize="sm" color="gray.400">
                                    Updates every 10 seconds • Update #{updateCount + 1}
                                </Text>
                                {!isLoading && (
                                    <Badge
                                        colorScheme="blue"
                                        variant="outline"
                                        px={2}
                                        py={1}
                                        borderRadius="md"
                                    >
                                        Next update in {nextUpdateIn}s
                                    </Badge>
                                )}
                            </HStack>
                        </Box>

                        <Box
                            bg={cardBg}
                            borderRadius="xl"
                            boxShadow="xl"
                            overflow="hidden"
                            border="1px"
                            borderColor={borderColor}
                            animation="slideInUp 1s ease-out 0.4s both"
                            data-leaderboard-table
                            sx={{
                                '@keyframes slideInUp': {
                                    from: { transform: 'translateY(50px)', opacity: 0 },
                                    to: { transform: 'translateY(0)', opacity: 1 }
                                },
                                '@keyframes flashUpdate': {
                                    '0%': { boxShadow: 'xl' },
                                    '50%': { boxShadow: '0 0 30px rgba(72, 187, 120, 0.6)', borderColor: 'green.400' },
                                    '100%': { boxShadow: 'xl' }
                                }
                            }}
                        >
                            <TableContainer>
                                <Table variant="simple" size="lg">
                                    <Thead bg={headerBg}>
                                        <Tr>
                                            <Th textAlign="center" color={textColor} fontSize="md">Rank</Th>
                                            <Th color={textColor} fontSize="md">Username</Th>
                                            <Th color={textColor} fontSize="md">Amount Won</Th>
                                            <Th color={textColor} fontSize="md">Game Played</Th>
                                            <Th textAlign="center" color={textColor} fontSize="md">Ranking</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {isLoading ? (
                                            // ...existing skeleton code...
                                            Array.from({ length: 10 }).map((_, index) => (
                                                <Tr key={index}>
                                                    <Td textAlign="center">
                                                        <Skeleton height="40px" width="40px" mx="auto" />
                                                    </Td>
                                                    <Td>
                                                        <VStack align="start" spacing={2}>
                                                            <Skeleton height="20px" width="120px" />
                                                            <Skeleton height="16px" width="100px" />
                                                        </VStack>
                                                    </Td>
                                                    <Td>
                                                        <Skeleton height="24px" width="100px" />
                                                    </Td>
                                                    <Td>
                                                        <Skeleton height="20px" width="140px" />
                                                    </Td>
                                                    <Td textAlign="center">
                                                        <Skeleton height="24px" width="80px" mx="auto" />
                                                    </Td>
                                                </Tr>
                                            ))
                                        ) : (
                                            leaderboardData.map((player, index) => (
                                                <Tr
                                                    key={player.username}
                                                    _hover={{
                                                        bg: hoverBg,
                                                        transform: "translateY(-2px)",
                                                        boxShadow: "lg"
                                                    }}
                                                    transition="all 0.3s ease"
                                                    animation={`slideInLeft${index} 0.6s ease-out ${index * 0.1}s both`}
                                                    cursor="pointer"
                                                    sx={{
                                                        [`@keyframes slideInLeft${index}`]: {
                                                            from: { transform: 'translateX(-100%)', opacity: 0 },
                                                            to: { transform: 'translateX(0)', opacity: 1 }
                                                        }
                                                    }}
                                                >
                                                    <Td textAlign="center">
                                                        <HStack justify="center">
                                                            {getRankIcon(player.rank)}
                                                        </HStack>
                                                    </Td>
                                                    <Td>
                                                        <VStack align="start" spacing={1}>
                                                            <Text
                                                                fontWeight="bold"
                                                                fontSize="lg"
                                                                color={textColor}
                                                            >
                                                                {player.username}
                                                            </Text>
                                                            <Text
                                                                fontSize="sm"
                                                                color="gray.500"
                                                            >
                                                                {player.fullName}
                                                            </Text>
                                                        </VStack>
                                                    </Td>
                                                    <Td>
                                                        <Text
                                                            fontSize="xl"
                                                            fontWeight="bold"
                                                            color={player.winAmount >= 25000 ? "gold" : "green.400"}
                                                            animation="countUp 0.8s ease-out"
                                                            textShadow={player.winAmount >= 25000 ? "0 0 10px gold" : "none"}
                                                            sx={{
                                                                '@keyframes countUp': {
                                                                    from: { opacity: 0, transform: 'scale(0.5)' },
                                                                    to: { opacity: 1, transform: 'scale(1)' }
                                                                }
                                                            }}
                                                        >
                                                            ${(animatedAmounts[player.username] || player.winAmount).toLocaleString('en-US')}
                                                            {player.winAmount >= 25000 && (
                                                                <Text
                                                                    as="span"
                                                                    fontSize="sm"
                                                                    color="gold"
                                                                    ml={2}
                                                                    animation="pulse 1.5s infinite"
                                                                    sx={{
                                                                        '@keyframes pulse': {
                                                                            '0%, 100%': { opacity: 1 },
                                                                            '50%': { opacity: 0.5 }
                                                                        }
                                                                    }}
                                                                >
                                                                    🎉 JACKPOT!
                                                                </Text>
                                                            )}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <Text
                                                            fontSize="md"
                                                            color={textColor}
                                                        >
                                                            {player.game}
                                                        </Text>
                                                    </Td>
                                                    <Td textAlign="center">
                                                        <Box>
                                                            {getRankLevel(player.rank)}
                                                        </Box>
                                                    </Td>
                                                </Tr>
                                            ))
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Box>

                        {/*<Box*/}
                        {/*    bg={cardBg}*/}
                        {/*    borderRadius="xl"*/}
                        {/*    boxShadow="xl"*/}
                        {/*    overflow="hidden"*/}
                        {/*    border="1px"*/}
                        {/*    borderColor={borderColor}*/}
                        {/*    animation="slideInUp 1s ease-out 0.4s both"*/}
                        {/*    data-leaderboard-table*/}
                        {/*    sx={{*/}
                        {/*        '@keyframes slideInUp': {*/}
                        {/*            from: { transform: 'translateY(50px)', opacity: 0 },*/}
                        {/*            to: { transform: 'translateY(0)', opacity: 1 }*/}
                        {/*        },*/}
                        {/*        '@keyframes flashUpdate': {*/}
                        {/*            '0%': { boxShadow: 'xl' },*/}
                        {/*            '50%': { boxShadow: '0 0 30px rgba(72, 187, 120, 0.6)', borderColor: 'green.400' },*/}
                        {/*            '100%': { boxShadow: 'xl' }*/}
                        {/*        }*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    <TableContainer overflowX="auto">*/}
                        {/*        <Table variant="simple" size="sm">*/}
                        {/*            <Thead bg={headerBg}>*/}
                        {/*                <Tr>*/}
                        {/*                    <Th textAlign="center" color={textColor} fontSize={{ base: "xs", md: "md" }}>Rank</Th>*/}
                        {/*                    <Th color={textColor} fontSize={{ base: "xs", md: "md" }}>Username</Th>*/}
                        {/*                    <Th color={textColor} fontSize={{ base: "xs", md: "md" }}>Amount Won</Th>*/}
                        {/*                    <Th display={{ base: "none", md: "table-cell" }} color={textColor} fontSize="md">Game Played</Th>*/}
                        {/*                    <Th textAlign="center" color={textColor} fontSize={{ base: "xs", md: "md" }}>Status</Th>*/}
                        {/*                </Tr>*/}
                        {/*            </Thead>*/}
                        {/*            <Tbody>*/}
                        {/*                {isLoading ? (*/}
                        {/*                    Array.from({ length: 10 }).map((_, index) => (*/}
                        {/*                        <Tr key={index}>*/}
                        {/*                            <Td textAlign="center">*/}
                        {/*                                <Skeleton height="40px" width="40px" mx="auto" />*/}
                        {/*                            </Td>*/}
                        {/*                            <Td>*/}
                        {/*                                <VStack align="start" spacing={2}>*/}
                        {/*                                    <Skeleton height="20px" width="120px" />*/}
                        {/*                                    <Skeleton height="16px" width="100px" />*/}
                        {/*                                </VStack>*/}
                        {/*                            </Td>*/}
                        {/*                            <Td>*/}
                        {/*                                <Skeleton height="24px" width="100px" />*/}
                        {/*                            </Td>*/}
                        {/*                            <Td display={{ base: "none", md: "table-cell" }}>*/}
                        {/*                                <Skeleton height="20px" width="140px" />*/}
                        {/*                            </Td>*/}
                        {/*                            <Td textAlign="center">*/}
                        {/*                                <Skeleton height="24px" width="80px" mx="auto" />*/}
                        {/*                            </Td>*/}
                        {/*                        </Tr>*/}
                        {/*                    ))*/}
                        {/*                ) : (*/}
                        {/*                    leaderboardData.map((player, index) => (*/}
                        {/*                        <Tr*/}
                        {/*                            key={player.username}*/}
                        {/*                            _hover={{*/}
                        {/*                                bg: hoverBg,*/}
                        {/*                                transform: "translateY(-2px)",*/}
                        {/*                                boxShadow: "lg"*/}
                        {/*                            }}*/}
                        {/*                            transition="all 0.3s ease"*/}
                        {/*                            cursor="pointer"*/}
                        {/*                        >*/}
                        {/*                            <Td textAlign="center" fontSize={{ base: "xs", md: "lg" }}>*/}
                        {/*                                <HStack justify="center">{getRankIcon(player.rank)}</HStack>*/}
                        {/*                            </Td>*/}
                        {/*                            <Td>*/}
                        {/*                                <VStack align="start" spacing={1}>*/}
                        {/*                                    <Text fontWeight="bold" fontSize={{ base: "sm", md: "lg" }} color={textColor}>*/}
                        {/*                                        {player.username}*/}
                        {/*                                    </Text>*/}
                        {/*                                    <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">*/}
                        {/*                                        {player.fullName}*/}
                        {/*                                    </Text>*/}
                        {/*                                </VStack>*/}
                        {/*                            </Td>*/}
                        {/*                            <Td>*/}
                        {/*                                <Text*/}
                        {/*                                    fontSize={{ base: "sm", md: "xl" }}*/}
                        {/*                                    fontWeight="bold"*/}
                        {/*                                    color={player.winAmount >= 25000 ? "gold" : "green.400"}*/}
                        {/*                                    textShadow={player.winAmount >= 25000 ? "0 0 10px gold" : "none"}*/}
                        {/*                                >*/}
                        {/*                                    ${(animatedAmounts[player.username] || player.winAmount).toLocaleString('en-US')}*/}
                        {/*                                    {player.winAmount >= 25000 && (*/}
                        {/*                                        <Text*/}
                        {/*                                            as="span"*/}
                        {/*                                            fontSize={{ base: "xs", md: "sm" }}*/}
                        {/*                                            color="gold"*/}
                        {/*                                            ml={1}*/}
                        {/*                                        >*/}
                        {/*                                            🎉 JACKPOT!*/}
                        {/*                                        </Text>*/}
                        {/*                                    )}*/}
                        {/*                                </Text>*/}
                        {/*                            </Td>*/}
                        {/*                            <Td display={{ base: "none", md: "table-cell" }} fontSize="sm">*/}
                        {/*                                {player.game}*/}
                        {/*                            </Td>*/}
                        {/*                            <Td textAlign="center" fontSize={{ base: "xs", md: "sm" }}>*/}
                        {/*                                {getRankBadge(player.rank)}*/}
                        {/*                            </Td>*/}
                        {/*                        </Tr>*/}
                        {/*                    ))*/}
                        {/*                )}*/}
                        {/*            </Tbody>*/}
                        {/*        </Table>*/}
                        {/*    </TableContainer>*/}
                        {/*</Box>*/}

                        {/* Live Update Indicator */}
                        <Box textAlign="center" mt={6}>
                            <Text
                                fontSize="sm"
                                color="gray.500"
                                animation="pulse 2s infinite"
                                sx={{
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.5 }
                                    }
                                }}
                            >
                                🔴 Live • Updated in real-time
                            </Text>
                        </Box>
                    </VStack>
                </Container>
            </Box>
        </>
        // <Box bg={bg} minH="100vh" py={8}>

    );
}
